/**
 * React Query hooks for Dashboard & Reporting System
 * Phase 6 Batch 3 - All 8 Reports (Core 4 + EOS Add-ons)
 */
import { useQuery } from '@tanstack/react-query';

const API_BASE = import.meta.env.VITE_API_URL;
if (!API_BASE) {
  throw new Error('VITE_API_URL environment variable is required. Check your .env file.');
}

// Helper to get auth token
async function getAuthToken(): Promise<string> {
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

// Helper for authenticated API calls
async function fetchWithAuth(endpoint: string) {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed: ${response.statusText}`);
  }

  return response.json();
}

// Dashboard Stats Hook
export function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: () => fetchWithAuth('/api/dashboard/stats'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Revenue by Month Hook
export function useRevenueByMonth(months: number = 6) {
  return useQuery({
    queryKey: ['dashboard', 'revenue-by-month', months],
    queryFn: () => fetchWithAuth(`/api/dashboard/revenue-by-month?months=${months}`),
    staleTime: 5 * 60 * 1000,
  });
}

// Projects by Stage Hook
export function useProjectsByStage() {
  return useQuery({
    queryKey: ['dashboard', 'projects-by-stage'],
    queryFn: () => fetchWithAuth('/api/dashboard/projects-by-stage'),
    staleTime: 5 * 60 * 1000,
  });
}

// Outstanding Invoices Hook
export function useOutstandingInvoices() {
  return useQuery({
    queryKey: ['dashboard', 'outstanding-invoices'],
    queryFn: () => fetchWithAuth('/api/dashboard/outstanding-invoices'),
    staleTime: 2 * 60 * 1000, // 2 minutes (more frequent for billing data)
  });
}

// Type definitions for API responses
export interface DashboardStats {
  this_month_revenue: number;
  this_month_paid: number;
  outstanding_amount: number;
  available_to_invoice: number;
  active_projects: number;
  proposal_count: number;
  in_permit_count: number;
  draft_invoices: number;
  sent_invoices: number;
  overdue_invoices: number;
}

export interface RevenueMonth {
  month: string;
  month_label: string;
  revenue: number;
  invoices: number;
  paid: number;
  outstanding: number;
}

export interface ProjectStage {
  stage: string;
  stage_label: string;
  count: number;
  unbilled_value: number;
  percentage: number;
}

export interface OutstandingInvoice {
  invoice_id: string;
  invoice_number: string;
  project_number: number | null;  // Phase 9.6a: Can be null for proposals
  project_name: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  total_amount: number;
  invoice_date: string;
  due_date: string;
  days_outstanding: number;
  days_overdue: number | null;
  status: 'current' | 'overdue';
}

export interface OutstandingInvoicesSummary {
  total_outstanding: number;
  total_overdue: number;
  count: number;
  overdue_count: number;
}

// ====== EOS Add-on Reports ======

// Revenue Pipeline Hook
export function useRevenuePipeline(months: number = 6) {
  return useQuery({
    queryKey: ['dashboard', 'revenue-pipeline', months],
    queryFn: () => fetchWithAuth(`/api/dashboard/revenue-pipeline?months=${months}`),
    staleTime: 5 * 60 * 1000,
  });
}

// Cash Flow Forecast Hook
export function useCashFlowForecast(days: number = 90) {
  return useQuery({
    queryKey: ['dashboard', 'cash-flow-forecast', days],
    queryFn: () => fetchWithAuth(`/api/dashboard/cash-flow-forecast?days=${days}`),
    staleTime: 5 * 60 * 1000,
  });
}

// Project Profitability Hook
export function useProjectProfitability(status: string = 'all') {
  return useQuery({
    queryKey: ['dashboard', 'project-profitability', status],
    queryFn: () => fetchWithAuth(`/api/dashboard/project-profitability?status=${status}`),
    staleTime: 5 * 60 * 1000,
  });
}

// Ball in Court Hook
export function useBallInCourt() {
  return useQuery({
    queryKey: ['dashboard', 'ball-in-court'],
    queryFn: () => fetchWithAuth('/api/dashboard/ball-in-court'),
    staleTime: 2 * 60 * 1000, // 2 minutes (action items need frequent updates)
  });
}

// Financial Summary Hook (9.9b)
export function useFinancialSummary() {
  return useQuery({
    queryKey: ['dashboard', 'financial-summary'],
    queryFn: () => fetchWithAuth('/api/dashboard/financial-summary'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Monthly Proposal Activity Hook (SPEC-monthly-proposal-activity, issue #151)
export function useProposalActivity(months: number = 12) {
  return useQuery<ProposalActivityData>({
    queryKey: ['dashboard', 'proposal-activity', months],
    queryFn: () => fetchWithAuth(`/api/dashboard/proposal-activity?months=${months}`),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Type definitions for EOS Add-on API responses

export interface ScheduledWorkMonth {
  month: string;
  month_label: string;
  scheduled_amount: number;
  project_count: number;
}

export interface ProposalCategory {
  count: number;
  total_value: number;
  regular_value: number;
  optional_value: number;
  age_category: string;
}

export interface RevenuePipelineData {
  scheduled_work: ScheduledWorkMonth[];
  proposals: {
    active: ProposalCategory;
    aging: ProposalCategory;
    stale: ProposalCategory;
  };
  summary: {
    total_scheduled: number;
    total_proposals: number;
    total_pipeline: number;
  };
}

export interface ExpectedIncoming {
  week_number: number;
  week_label: string;
  due_amount: number;
  invoice_count: number;
}

export interface ScheduledWorkWeek {
  month: string;
  month_label: string;
  scheduled_amount: number;
  project_count: number;
}

export interface CashFlowForecastData {
  expected_incoming: ExpectedIncoming[];
  available_to_bill: {
    total_amount: number;
    project_count: number;
    if_invoiced_this_week: {
      adds_to_month: string;
      estimated_amount: number;
    };
  };
  scheduled_work: ScheduledWorkWeek[];
  summary: {
    total_expected_incoming: number;
    total_available_to_bill: number;
    total_scheduled_work: number;
    net_forecast: number;
  };
}

export interface ProjectProfitabilityRow {
  project_id: string;
  project_number: number | null;  // Phase 9.6a: Can be null for proposals
  project_name: string;
  client_name: string;
  workflow_stage: string;
  status: string;
  confirmed_fees: number;
  invoiced_amount: number;
  invoiced_percentage: number;
  collected_amount: number;
  collection_rate: number;
  unbilled_amount: number;
  health_status: 'healthy' | 'at_risk' | 'over_budget';
}

export interface ProjectProfitabilitySummary {
  project_count: number;
  total_confirmed: number;
  total_invoiced: number;
  total_collected: number;
  total_unbilled: number;
  avg_collection_rate: number;
}

export interface ProjectProfitabilityData {
  data: ProjectProfitabilityRow[];
  summary_by_stage: Record<string, ProjectProfitabilitySummary>;
}

export interface BallInCourtItem {
  scope_id: string;
  project_id: string;  // Added for navigation (9.4d MED-03)
  project_number: number | null;  // Phase 9.6a: Can be null for proposals
  project_name: string;
  scope_name: string;
  organization_name: string;
  status: 'waiting_on_us' | 'waiting_on_them';
  status_notes: string | null;
  status_updated_at: string;
  days_waiting: number;
  urgency: 'low' | 'medium' | 'high';
}

export interface BallInCourtData {
  waiting_on_us: BallInCourtItem[];
  waiting_on_them: BallInCourtItem[];
  summary: {
    waiting_on_us_count: number;
    waiting_on_us_urgent: number;
    waiting_on_them_count: number;
    waiting_on_them_urgent: number;
  };
}

// Financial Summary types (9.9b)
export interface FinancialSummaryCategory {
  count: number;
  total_value: number;
  outstanding: number;
}

export interface FinancialSummaryData {
  proposals: {
    count: number;
    total_value: number;
  };
  contracts: Record<string, FinancialSummaryCategory>;
  revenue: {
    billed_all_time: number;
    billed_ytd: number;
    collected_all_time: number;
    outstanding_all_time: number;
  };
}

// Monthly Proposal Activity types (SPEC-monthly-proposal-activity, issue #151)
// One entry per month in the trailing window, chronological ascending (oldest
// first), zero-filled. The LAST element is the current month.
export interface ProposalActivityMonth {
  month: string;        // ISO date, 1st of the month
  month_label: string;  // e.g. "May 2026"
  prepared_count: number;
  prepared_value: number;
  won_count: number;
  won_value: number;
}

export interface ProposalActivityData {
  months: ProposalActivityMonth[];
}

// ==============================================================================
// DRILLDOWN REPORT TYPES - Phase 9.38
// Types for dashboard stat drilldown reports
// ==============================================================================

// Available to Invoice by Project
export interface AvailableToInvoiceByProjectRow {
  project_id: string;
  project_number: number | null;
  project_name: string;
  client_name: string | null;
  unbilled_amount: number;
  item_count: number;
  oldest_item_date: string | null;
}

export interface AvailableToInvoiceByProjectSummary {
  total_unbilled: number;
  total_items: number;
  project_count: number;
}

export interface AvailableToInvoiceByProjectData {
  data: AvailableToInvoiceByProjectRow[];
  summary: AvailableToInvoiceByProjectSummary;
}

// Outstanding by Project
// D1 G1 follow-up (V181 cohort): client_name -> client_names (STRING_AGG of
// distinct invoice client names per project, primary-first ordered).
// One row per project so the React table key (project_id) stays unique on
// multi-client projects.
export interface OutstandingByProjectRow {
  project_id: string;
  project_number: number | null;
  project_name: string;
  client_names: string | null;
  outstanding_amount: number;
  overdue_amount: number;
  invoice_count: number;
  oldest_invoice_date: string | null;
}

export interface OutstandingByProjectSummary {
  total_outstanding: number;
  total_overdue: number;
  invoice_count: number;
  project_count: number;
}

export interface OutstandingByProjectData {
  data: OutstandingByProjectRow[];
  summary: OutstandingByProjectSummary;
}

// YTD Revenue by Project
export interface YTDRevenueByProjectRow {
  project_id: string;
  project_number: number | null;
  project_name: string;
  client_name: string | null;
  collected_amount: number;
  invoice_count: number;
  last_payment_date: string | null;
}

export interface YTDRevenueByProjectSummary {
  total_collected: number;
  invoice_count: number;
  project_count: number;
}

export interface YTDRevenueByProjectData {
  data: YTDRevenueByProjectRow[];
  summary: YTDRevenueByProjectSummary;
}

// Overdue Aging
export interface OverdueAgingRow {
  invoice_id: string;
  invoice_number: string;
  project_number: number | null;
  project_name: string;
  client_name: string | null;
  total_amount: number;
  due_date: string;
  days_overdue: number;
  aging_bucket: '1-30' | '31-60' | '61-90' | '90+';
}

export interface OverdueAgingBuckets {
  '1-30': number;
  '31-60': number;
  '61-90': number;
  '90+': number;
}

export interface OverdueAgingSummary {
  total_overdue: number;
  invoice_count: number;
}

export interface OverdueAgingData {
  data: OverdueAgingRow[];
  buckets: OverdueAgingBuckets;
  summary: OverdueAgingSummary;
}

// Pipeline Breakdown
export interface PipelineProposalRow {
  project_id: string;
  project_number: number | null;
  project_name: string;
  client_name: string | null;
  regular_value: number;
  optional_value: number;
  total_value: number;
  days_since_sent: number;
  category: 'active' | 'aging' | 'stale';
}

export interface PipelineScheduledWorkRow {
  month: string;
  month_label: string;
  scheduled_amount: number;
}

export interface PipelineBreakdownSummary {
  total_pipeline: number;
  proposal_count: number;
  proposal_total: number;
  scheduled_total: number;
}

export interface PipelineBreakdownData {
  proposals: {
    active: PipelineProposalRow[];
    aging: PipelineProposalRow[];
    stale: PipelineProposalRow[];
  };
  scheduled_work: PipelineScheduledWorkRow[];
  summary: PipelineBreakdownSummary;
}

// Metrics by Client
export interface MetricsByClientRow {
  organization_id: string;
  organization_name: string;
  available_to_invoice: number;
  outstanding: number;
  overdue: number;
  ytd_revenue: number;
  project_count: number;
}

export interface MetricsByClientSummary {
  total_available_to_invoice: number;
  total_outstanding: number;
  client_count: number;
}

export interface MetricsByClientData {
  data: MetricsByClientRow[];
  summary: MetricsByClientSummary;
}

// Cash Flow by Week
export interface CashFlowByWeekRow {
  week_start: string;
  week_label: string;
  expected_amount: number;
  invoice_count: number;
}

export interface CashFlowByWeekSummary {
  total_expected: number;
}

export interface CashFlowByWeekData {
  data: CashFlowByWeekRow[];
  summary: CashFlowByWeekSummary;
}

// ==============================================================================
// DRILLDOWN REPORT HOOKS - Phase 9.38
// ==============================================================================

// Available to Invoice by Project Hook
export function useAvailableToInvoiceByProject() {
  return useQuery<AvailableToInvoiceByProjectData>({
    queryKey: ['dashboard', 'available-to-invoice-by-project'],
    queryFn: () => fetchWithAuth('/api/dashboard/available-to-invoice-by-project'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Outstanding by Project Hook
export function useOutstandingByProject() {
  return useQuery<OutstandingByProjectData>({
    queryKey: ['dashboard', 'outstanding-by-project'],
    queryFn: () => fetchWithAuth('/api/dashboard/outstanding-by-project'),
    staleTime: 2 * 60 * 1000, // 2 minutes (billing data)
  });
}

// YTD Revenue by Project Hook
export function useYTDRevenueByProject() {
  return useQuery<YTDRevenueByProjectData>({
    queryKey: ['dashboard', 'ytd-revenue-by-project'],
    queryFn: () => fetchWithAuth('/api/dashboard/ytd-revenue-by-project'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Overdue Aging Hook
export function useOverdueAging() {
  return useQuery<OverdueAgingData>({
    queryKey: ['dashboard', 'overdue-aging'],
    queryFn: () => fetchWithAuth('/api/dashboard/overdue-aging'),
    staleTime: 2 * 60 * 1000, // 2 minutes (billing data)
  });
}

// Pipeline Breakdown Hook
export function usePipelineBreakdown() {
  return useQuery<PipelineBreakdownData>({
    queryKey: ['dashboard', 'pipeline-breakdown'],
    queryFn: () => fetchWithAuth('/api/dashboard/pipeline-breakdown'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Metrics by Client Hook
export function useMetricsByClient() {
  return useQuery<MetricsByClientData>({
    queryKey: ['dashboard', 'metrics-by-client'],
    queryFn: () => fetchWithAuth('/api/dashboard/metrics-by-client'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Cash Flow by Week Hook
export function useCashFlowByWeek(weeks: number = 8) {
  return useQuery<CashFlowByWeekData>({
    queryKey: ['dashboard', 'cash-flow-by-week', weeks],
    queryFn: () => fetchWithAuth(`/api/dashboard/cash-flow-by-week?weeks=${weeks}`),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ==============================================================================
// TAX ESTIMATE HOOKS — R1 GST Remittance, R2 Corp Tax, R3 Draft Invoices
// ==============================================================================

export interface GstRemittanceRecord {
  id: string;
  period_start: string;
  period_end: string;
  gst_collected: number;
  gst_remitted: number | null;
  remittance_date: string | null;
  status: 'pending' | 'remitted';
  notes: string | null;
}

export interface GstSummaryData {
  gst_estimated: number;
  invoice_count: number;
  period_start: string;
  period_end: string;
  next_remittance_date: string;
  days_until_remittance: number;
  reminder_active: boolean;
  gst_remittance_period: 'quarterly' | 'annual';
  history: GstRemittanceRecord[];
}

export interface CorpTaxData {
  total_billed: number;
  corp_tax_rate: number;
  estimated_corp_tax: number;
  invoice_count: number;
  fy_start: string;
  corp_tax_remittance_dates: string[];
}

export interface DraftInvoiceItem {
  invoice_id: string;
  invoice_number: string;
  project_name: string;
  project_number: number | null;
  client_name: string;
  total_amount: number;
  invoice_date: string | null;
  created_at: string | null;
}

export interface DraftInvoicesData {
  data: DraftInvoiceItem[];
  count: number;
}

// GST Summary Hook (R1)
export function useGstSummary() {
  return useQuery<GstSummaryData>({
    queryKey: ['dashboard', 'gst-summary'],
    queryFn: () => fetchWithAuth('/api/dashboard/gst-summary'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Corp Tax Estimate Hook (R2)
export function useCorpTax() {
  return useQuery<CorpTaxData>({
    queryKey: ['dashboard', 'corp-tax'],
    queryFn: () => fetchWithAuth('/api/dashboard/corp-tax'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Draft Invoices Hook (R3)
export function useDraftInvoices() {
  return useQuery<DraftInvoicesData>({
    queryKey: ['dashboard', 'draft-invoices'],
    queryFn: () => fetchWithAuth('/api/dashboard/draft-invoices'),
    staleTime: 2 * 60 * 1000, // 2 minutes (billing data changes frequently)
  });
}
